import { useState } from 'react';
import styled, { useTheme } from 'styled-components';
import { BodySmall, BodyMedium } from '../Texts';
import Dropdown from '../Dropdown';
import Button from '../Button';
import { useLocation, useNavigate } from 'react-router-dom';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { selectTotalWalletBalanceUSD } from '../../redux/slices/tokenSlice';
import { IonButtons, IonMenuButton } from '@ionic/react';
import WalletConnectButton from '../WalletConnect';
import { useFlag } from '@unleash/proxy-client-react';
import Notifications from './Notifications';
import { formatUSD } from '../../common/currency';
import { hapticsImpactLight } from '../../common/haptic';
import useCustomLogin from '../../hooks/useCustomLogin';
import DropdownContent from './DropdownContent';
import { useSelector } from 'react-redux';
import { Capacitor } from '@capacitor/core';
import ThemeToggle from '../ThemeToggle/index';
import { selectThemeMode } from '../../redux/slices/themeSlice';

const NavContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const NavUserProfileContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  @media ${PHONE_MEDIA_QUERY} {
    margin-left: auto;
    justify-content: flex-end;
    width: 100%;
  }
`;

const ProfileTrigger = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const BalanceContainer = styled.div`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  padding: 10px;
  background: ${({ theme }) => theme.background.secondary};
  min-height: 46px;
  border-radius: 8px;
`;

const BlackLayer = styled.div`
  width: 101vw;
  height: 100vh;
  background-color: ${({ theme }) => theme.background.overlay};
  top: 100%;
  left: -16px;
  right: 0px;
  bottom: 0px;
  z-index: 999;
  position: absolute;
  backdrop-filter: blur(2px);
`;

export const VerticalLine = styled.div`
  height: 20px;
  width: 1px;
  background-color: ${({ theme }) => theme.border.dashed};
`;

const BurgerMenuContainer = styled.div`
  margin-right: auto;
`;

const NavUserProfile = ({ user }) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const [isOpen, setIsOpen] = useState(false);
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);

  const totalBalance = useSelector(selectTotalWalletBalanceUSD);
  const { login } = useCustomLogin();
  const navigate = useNavigate();

  const location = useLocation();

  const isClaimPage = location.pathname.includes('claim'); // promo-claim or claim

  const enableWalletConnect =
    useFlag('enableWalletConnect') &&
    !Capacitor.isNativePlatform() &&
    !isClaimPage;

  const themeMode = useSelector(selectThemeMode);
  const theme = useTheme();

  return (
    <>
      <NavContainer>
        {(!isMobile || !user) && (
          <img
            style={{
              width: isMobile ? 40 : 150,
              cursor: 'pointer',
            }}
            src={
              isMobile
                ? '/mobile-logo.webp'
                : themeMode === 'light'
                  ? '/logo.webp'
                  : '/logo-dark.webp'
            }
            onClick={() => {
              hapticsImpactLight();
              if (user) {
                navigate('/app/home');
              } else {
                navigate('/');
              }
            }}
          />
        )}
        {!!user ? (
          <NavUserProfileContainer>
            {isMobile && (
              <BurgerMenuContainer
                onClick={() => {
                  hapticsImpactLight();
                }}
              >
                <IonButtons slot='start'>
                  <IonMenuButton
                    style={{ color: theme.text.regular }}
                  ></IonMenuButton>
                </IonButtons>
              </BurgerMenuContainer>
            )}
            {!isMobile && (
              <>
                <BodyMedium
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    hapticsImpactLight();
                    navigate('/rewards');
                  }}
                >
                  Rewards
                </BodyMedium>
                <VerticalLine />
              </>
            )}
            {enableWalletConnect && (
              <>
                <WalletConnectButton
                  onConnectedClick={() => {
                    setIsOpen(true);
                  }}
                />
              </>
            )}
            <Notifications
              isOpen={isNotificationsOpen}
              setIsOpen={setIsNotificationsOpen}
            />
            {!isMobile && (
              <>
                <VerticalLine />
                <Dropdown
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  trigger={
                    <ProfileTrigger>
                      <BodySmall>
                        {!isMobile ? (
                          <>
                            {(() => {
                              const email =
                                user?.email?.address ||
                                user?.google?.email ||
                                user?.email;

                              if (!email) return user?.phone?.number || '';

                              const [name, domain] = email.split('@');
                              return (
                                <>
                                  {name}
                                  <span style={{ userSelect: 'none' }}>
                                    &#8203;
                                  </span>
                                  <span>@</span>
                                  {domain}
                                </>
                              );
                            })()}
                          </>
                        ) : (
                          <>
                            <img
                              src='/navbar-user.webp'
                              width='24'
                              height='24'
                            />
                          </>
                        )}
                      </BodySmall>
                    </ProfileTrigger>
                  }
                >
                  <DropdownContent setIsOpen={setIsOpen} />
                </Dropdown>
              </>
            )}
          </NavUserProfileContainer>
        ) : (
          <NavUserProfileContainer style={{ gap: isMobile ? 10 : 30 }}>
            <a
              style={{ textDecoration: 'none', color: 'inherit' }}
              onClick={(e) => {
                navigate('/gift-cards');
              }}
            >
              <BodyMedium
                style={{
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  fontSize: 'clamp(12px, .5vw, 13px)',
                }}
              >
                Physical Gift Cards
              </BodyMedium>
            </a>
            <VerticalLine />
            {enableWalletConnect && <WalletConnectButton />}
            <Button
              onClick={() => {
                login();
              }}
            >
              Login
            </Button>
          </NavUserProfileContainer>
        )}
      </NavContainer>
      {isMobile && (isOpen || isNotificationsOpen) && <BlackLayer />}
    </>
  );
};

export default NavUserProfile;
